import { CSSProperties, useEffect, useState } from 'react'

import cn from 'clsx'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { getValueWithPercentage } from "@App/helpers/toPercentage";
import { useAppSelector } from '@App/hooks'
import useBalanceInfo, { BalanceIndicatorKey } from "@App/hooks/useBalanceInfo";
import { ArrowRight } from '@App/static/icons/AccountOrdersPageIcons'
import { ThreeEmptyDotsIcon } from '@App/static/icons/WalletPageIcons'

import { currentWallet } from '@Redux/slices/user/selectors'

import OutsideClickComponent from '../../../OutsideClickHandler'

import s from './Balance.module.scss'
import { BalanceSettings } from './components/BalanceSettings'

const balanceIndicatorNames: Record<BalanceIndicatorKey, string> = {
  available: 'Available',
  equity: 'Equity',
  funds: 'Funds',
  pnl: 'P&L',
  margin: 'Margin',
  margin_level: 'Margin level'
}

export type BalanceOption = {
  key: string
  name: string
  value: number
  highlightValue?: boolean
  isActive: boolean
  type: 'currency' | 'percentage'
}

export const Balance = () => {
  const currWallet = useAppSelector(currentWallet)
  const balanceIndicators  = useBalanceInfo(currWallet)

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [options, setOptions] = useState<BalanceOption[]>(balanceIndicators.map(
    (b) => ({ ...b,
      isActive: b.key !== 'margin_level' && true,
      highlightValue: b.key === 'pnl',
      name: balanceIndicatorNames[b.key as BalanceIndicatorKey],
      type: b.key === 'margin_level' ? 'percentage' : 'currency',
    }))
  )

  const [isExtended, setIsExtended] = useState(false)
  const [containerOverflow, setContainerOverflow] = useState<'hidden' | 'visible'>('visible')
  const toggleExtended = () => {
    setIsExtended((prev) => !prev)
    setContainerOverflow('hidden')

    setTimeout(() => {
      setContainerOverflow('visible')
    }, 1200)
  }
  const toggleSettings = () => {
    setIsSettingsOpen((prev) => !prev)
  }
  const setOptionStatus = (option: string, status: boolean) => {
    setOptions((prev) => prev.map((o) => (o.name === option ? { ...o, isActive: status } : o)))
  }

  useEffect(() => {
    setOptions((prevOptions) =>
      prevOptions.map((option) => {
        const newValue = balanceIndicators.find((b) => b.key === option.key)
        if (newValue) {
          return { ...option, value: newValue.value }
        }
        return option
      }),
    )
  }, balanceIndicators)

  useEffect(() => {
    if (currWallet) {
      setIsExtended(true)
    }
  }, [currWallet])
  if (!currWallet?.currency_code) {
    return <></>
  }
  let digits = String(Math.abs(currWallet.amount)).replace('.', '').length
  if (digits < 4) {
    digits = 4
  }
  const marginLevelInfo = options.find((b) => b.key === 'margin_level')
  return (
    <div
      className={cn(s.container, isExtended && s.extended, !isExtended && s.unExtended)}
      style={{ overflow: containerOverflow }}
    >
      <div className={s.arrowIconContainer}>
        <ArrowRight className={cn(s.arrowIcon, isExtended && s.extended)} onClick={toggleExtended} />
      </div>
      <div className={s.content}>
        <div className={s.unextendedContainer} onClick={toggleExtended}>
          Balance
        </div>
        <div className={s.extendedContainer}>
          <div className={s.balaces}>
            {options.map(
              (bal) =>
                bal.isActive && (
                  <div key={bal.name} className={s.balace}>
                    <span className={s.name}>{bal.name}</span>
                    <div>
                      <span
                        className={cn(
                          s.value,
                          bal.highlightValue && (bal.value < 0 ? cn(s.highlight, s.red) : cn(s.highlight, s.blue)),
                        )}
                      >
                        {bal.type === 'currency'
                          ? getValueWithCurrency(currWallet.currency_code, bal.value)
                          : getValueWithPercentage(bal.value)}
                      </span>

                      {bal.key === 'margin' && marginLevelInfo?.value ? (
                        <span className={s.subValue}>{getValueWithPercentage(marginLevelInfo.value)}</span>
                      ) : null}
                    </div>
                  </div>
                ),
            )}
          </div>
          <OutsideClickComponent handler={() => setIsSettingsOpen(false)} className={s.dotsIconContainer}>
            <ThreeEmptyDotsIcon className={s.dotsIcon} onClick={toggleSettings} />
            {isSettingsOpen && (
              <div className={s.balanceSettings}>
                <BalanceSettings
                  options={options}
                  setOptionStatus={setOptionStatus}
                  currencyCode={currWallet.currency_code}
                />
              </div>
            )}
          </OutsideClickComponent>
        </div>
      </div>
    </div>
  )
}
